/**
 * =============================================================================
 * COMPONENT:  SearchTermsImport.js
 * FUNKTION:   Suchbegriffe per Copy-Paste importieren, Verlauf + Diff
 * VERSION:    v1.0
 * DATUM:      2026-03-05
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import AdsDecisions from './AdsDecisions';
import './imports-shared.css';
import './SearchTermsImport.css';

var API = '/api/ads_searchterms_import.php';

export default function SearchTermsImport({ campaignId, campaignName, selectedIds, campaignList, von1, bis1 }) {
    var [data, setData]           = useState([]);
    var [loading, setLoading]     = useState(false);
    var [showForm, setShowForm]   = useState(false);
    var [importing, setImporting] = useState(false);
    var [preview, setPreview]     = useState(null);
    var [importMsg, setImportMsg] = useState(null);
    var [datumVon, setDatumVon]   = useState(von1 || '');
    var [datumBis, setDatumBis]   = useState(bis1 || '');
    var [rawText, setRawText]     = useState('');
    var [filterKlicks, setFilterKlicks] = useState(false);

    var token = localStorage.getItem('token') || '';

    var load = useCallback(function() {
        if (!campaignId) return;
        setLoading(true);
        fetch(API + '?campaign_id=' + campaignId + '&limit=500', {
            headers: { Authorization: 'Bearer ' + token }
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) setData(json.data || []);
            setLoading(false);
        })
        .catch(function() { setLoading(false); });
    }, [campaignId, token]);

    useEffect(function() { load(); }, [load]);
    useEffect(function() { setDatumVon(von1 || ''); setDatumBis(bis1 || ''); }, [von1, bis1]);

    function handlePreview() {
        if (!rawText || !datumVon || !datumBis) return;
        setImporting(true);
        setImportMsg(null);
        setPreview(null);
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({
                preview: 1, raw: rawText,
                datum_von: datumVon, datum_bis: datumBis,
                campaign_id: campaignId, campaign_name: campaignName,
            }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) setPreview(json.rows || []);
            else setImportMsg({ ok: false, text: json.message });
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    function handleSave() {
        if (!preview) return;
        setImporting(true);
        fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify({
                save: 1, raw: rawText,
                datum_von: datumVon, datum_bis: datumBis,
                campaign_id: campaignId, campaign_name: campaignName,
            }),
        })
        .then(function(r) { return r.json(); })
        .then(function(json) {
            if (json.success) {
                setImportMsg({ ok: true, text: json.imported + ' Einträge erfasst' });
                setPreview(null);
                setRawText('');
                setShowForm(false);
                load();
            } else {
                setImportMsg({ ok: false, text: json.message });
            }
            setImporting(false);
        })
        .catch(function(e) { setImportMsg({ ok: false, text: e.message }); setImporting(false); });
    }

    var periods = Array.from(new Set(data.map(function(d) { return d.datum_von + '__' + d.datum_bis; })));

    function renderTable(rows) {
        var filtered = filterKlicks ? rows.filter(function(r) { return (r.klicks || 0) > 0; }) : rows;
        if (filtered.length === 0) return <p className="imp-empty">Keine Einträge.</p>;
        return (
            <div className="imp-table-wrap">
                <table className="imp-table">
                    <thead>
                        <tr>
                            <th>Suchbegriff</th>
                            <th>Option</th>
                            <th>Keyword</th>
                            <th>Impr.</th>
                            <th>Klicks</th>
                            <th>CTR %</th>
                            <th>Kosten</th>
                            <th>CPC</th>
                            <th>Conv.</th>
                            <th>Oben %</th>
                            <th>Top %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(function(r, i) {
                            var hasClick = (r.klicks || 0) > 0;
                            var hasConv  = (r.conversions || 0) > 0;
                            return (
                                <tr key={i} className={'imp-row' + (hasConv ? ' st-conv' : hasClick ? ' st-click' : '')}>
                                    <td className="imp-term">{r.suchbegriff}</td>
                                    <td className="imp-opt">{r.keyword_option || '—'}</td>
                                    <td className="imp-kw">{r.keyword || '—'}</td>
                                    <td className="imp-num">{r.impressionen || 0}</td>
                                    <td className="imp-num">{r.klicks || 0}</td>
                                    <td className="imp-num">{r.ctr != null ? r.ctr + '%' : '—'}</td>
                                    <td className="imp-num">{r.kosten != null ? r.kosten + ' €' : '—'}</td>
                                    <td className="imp-num">{r.cpc != null ? r.cpc + ' €' : '—'}</td>
                                    <td className="imp-num">{r.conversions || 0}</td>
                                    <td className="imp-num">{r.impr_oben_pct != null ? r.impr_oben_pct + '%' : '—'}</td>
                                    <td className="imp-num">{r.impr_top_pct != null ? r.impr_top_pct + '%' : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="imp-wrap">
            <div className="imp-header">
                <span className="imp-title">🔍 Suchbegriffe</span>
                <div className="imp-header-actions">
                    <label className="imp-filter-label">
                        <input type="checkbox" checked={filterKlicks}
                            onChange={function(e) { setFilterKlicks(e.target.checked); }} />
                        Nur mit Klicks
                    </label>
                    <button className="imp-btn imp-btn-import" onClick={function() { setShowForm(function(s) { return !s; }); setPreview(null); setImportMsg(null); }}>
                        {showForm ? '✕ Abbrechen' : '+ Importieren'}
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="imp-form">
                    <p className="imp-hint">
                        Google Ads → Suchbegriffe → Zeitraum wählen → alle Zeilen kopieren → hier einfügen.
                    </p>
                    <div className="imp-form-row">
                        <div className="imp-field">
                            <label>Von</label>
                            <input type="date" value={datumVon} onChange={function(e) { setDatumVon(e.target.value); }} />
                        </div>
                        <div className="imp-field">
                            <label>Bis</label>
                            <input type="date" value={datumBis} onChange={function(e) { setDatumBis(e.target.value); }} />
                        </div>
                    </div>
                    <div className="imp-field">
                        <label>Daten einfügen (Tab-getrennt aus Google Ads)</label>
                        <textarea rows={8} placeholder="Hier einfügen…"
                            value={rawText} onChange={function(e) { setRawText(e.target.value); }} />
                    </div>
                    <div className="imp-form-actions">
                        <button className="imp-btn imp-btn-preview" onClick={handlePreview} disabled={importing || !rawText}>
                            {importing && !preview ? 'Prüfe…' : '🔍 Vorschau'}
                        </button>
                        {preview && (
                            <button className="imp-btn imp-btn-save" onClick={handleSave} disabled={importing}>
                                {importing ? 'Speichere…' : '💾 Erfassen (' + preview.length + ' Einträge)'}
                            </button>
                        )}
                        {importMsg && (
                            <span className={importMsg.ok ? 'imp-msg-ok' : 'imp-msg-err'}>
                                {importMsg.ok ? '✅ ' : '❌ '}{importMsg.text}
                            </span>
                        )}
                    </div>
                    {preview && preview.length > 0 && (
                        <div className="imp-preview-wrap">
                            <div className="imp-preview-title">Vorschau — {preview.length} Einträge</div>
                            {renderTable(preview)}
                        </div>
                    )}
                </div>
            )}

            {loading && <p className="imp-loading">Lädt…</p>}
            {!loading && data.length === 0 && (
                <p className="imp-empty">Noch keine Suchbegriffe. "+ Importieren" um zu starten.</p>
            )}

            {!loading && periods.map(function(period) {
                var parts = period.split('__');
                var dVon  = parts[0];
                var dBis  = parts[1];
                var rows  = data.filter(function(d) { return d.datum_von === dVon && d.datum_bis === dBis; });
                var total = rows.length;
                var mitKlick = rows.filter(function(r) { return (r.klicks || 0) > 0; }).length;
                var gesamtKosten = rows.reduce(function(s, r) { return s + (parseFloat(r.kosten) || 0); }, 0).toFixed(2);

                return (
                    <div key={period} className="imp-period">
                        <div className="imp-period-header">
                            <span>📅 {dVon} – {dBis}</span>
                            <span className="imp-period-stat">{total} Begriffe</span>
                            <span className="imp-period-stat">{mitKlick} mit Klick</span>
                            <span className="imp-period-stat">{gesamtKosten} € gesamt</span>
                        </div>
                        {renderTable(rows)}
                    </div>
                );
            })}

            {data.length > 0 && (
                <AdsDecisions
                    bereich="suchbegriffe"
                    selectedIds={selectedIds}
                    campaignList={campaignList}
                />
            )}
        </div>
    );
}